import { Button } from "../UIElements/Button";
import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "../UIElements/Icon";
import { IconButton } from "../UIElements/Button";

const ParticipatingStoresSelector = ({
  businesses,
  selectedStores,
  onChangeSelectedStores,
  onCommitSelectedStores,
}) => {
  const { t } = useTranslation("agentTranslations");
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const listboxId = `participating-stores-${useId()}`;
  const [isStoreDropdownOpen, setIsStoreDropdownOpen] = useState(false);
  const [storeSearch, setStoreSearch] = useState("");

  const filteredStores = useMemo(
    () =>
      businesses
        .filter((store) =>
          store.name.toLowerCase().includes(storeSearch.toLowerCase()),
        )
        .map((store) => ({
          id: store.id,
          name: store.name,
        })),
    [businesses, storeSearch],
  );

  const handleToggleStore = (storeId) => {
    onChangeSelectedStores((previous) => {
      if (previous.includes(storeId)) {
        return previous.filter((item) => item !== storeId);
      }
      return [...previous, storeId];
    });
  };

  const handleSelectAllStores = () => {
    const nextSelection = [...selectedStores];

    filteredStores.forEach((store) => {
      if (!nextSelection.includes(store.id)) {
        nextSelection.push(store.id);
      }
    });

    onChangeSelectedStores(nextSelection);
  };

  const handleSelectNoStores = () => {
    onChangeSelectedStores([]);
  };

  const handleCloseDropdown = () => {
    setIsStoreDropdownOpen(false);
    if (onCommitSelectedStores) {
      onCommitSelectedStores(selectedStores);
    }
  };

  const handleToggleDropdown = () => {
    if (isStoreDropdownOpen) {
      handleCloseDropdown();
      return;
    }
    setIsStoreDropdownOpen(true);
  };

  useEffect(() => {
    if (!isStoreDropdownOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      const container = containerRef.current;
      if (!container) {
        return;
      }

      if (container.contains(event.target)) {
        return;
      }

      handleCloseDropdown();
    };

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") {
        return;
      }
      handleCloseDropdown();
      queueMicrotask(() => triggerRef.current?.focus());
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [isStoreDropdownOpen, selectedStores]);

  const totalStores = businesses.length || 0;
  const allSelected = selectedStores.length === totalStores && totalStores > 0;

  return (
    <div className="ordering-discounts-stores">
      <label className="text-muted-foreground mb-1.5 block flex justify-between text-[length:var(--font-size-product-ui)] font-medium">
        <span>Participating Stores</span>
        <span className="text-muted-foreground text-xs">
          {allSelected
            ? "All stores selected"
            : `${selectedStores.length} stores selected`}
        </span>
      </label>

      <div ref={containerRef} className="relative">
        <button
          ref={triggerRef}
          type="button"
          onClick={handleToggleDropdown}
          aria-expanded={isStoreDropdownOpen}
          aria-haspopup="listbox"
          aria-controls={listboxId}
          className="border-border bg-background hover:border-border flex w-full items-center justify-between rounded-lg border px-4 py-3 text-left transition-colors"
        >
          <div className="flex items-center gap-2 overflow-hidden">
            <Icon
              name="Store"
              size={16}
              className="text-muted-foreground flex-shrink-0"
            />
            {selectedStores.length > 0 ? (
              <div className="flex gap-1 overflow-hidden">
                {allSelected ? (
                  <span className="text-primary text-sm font-medium">
                    All Locations Selected
                  </span>
                ) : (
                  selectedStores.slice(0, 2).map((storeId) => (
                    <span
                      key={storeId}
                      className="border-primary/30 bg-accent/40 text-primary rounded border px-2 py-0.5 text-xs whitespace-nowrap"
                    >
                      {businesses
                        .find((business) => business.id === storeId)
                        ?.name?.split(" ")
                        .slice(0, 2)
                        .join(" ")}
                      ...
                    </span>
                  ))
                )}
                {selectedStores.length > 2 && !allSelected && (
                  <span className="text-muted-foreground ml-1 py-0.5 text-xs">
                    +{selectedStores.length - 2} more
                  </span>
                )}
              </div>
            ) : (
              <span className="text-muted-foreground text-sm">
                Select stores that accept this discount...
              </span>
            )}
          </div>
          <Icon
            name="ChevronRight"
            size={16}
            className={`text-muted-foreground transition-transform ${
              isStoreDropdownOpen ? "rotate-90" : ""
            }`}
          />
        </button>

        {isStoreDropdownOpen && (
          <div
            id={listboxId}
            role="listbox"
            aria-multiselectable="true"
            className="animate-fade-in border-border bg-background absolute top-full right-0 left-0 z-[var(--layer-dropdown)] mt-2 flex max-h-96 flex-col overflow-hidden rounded-xl border shadow-xl"
          >
            <div className="border-border bg-background sticky top-0 z-10 border-b p-3">
              <div className="relative">
                <Icon
                  name="Search"
                  size={14}
                  className="text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2"
                />
                <input
                  ref={searchRef}
                  type="text"
                  value={storeSearch}
                  onChange={(event) => setStoreSearch(event.target.value)}
                  placeholder="Search stores..."
                  className="border-border bg-background placeholder:text-muted-foreground focus:border-primary text-foreground w-full rounded-lg border py-2 pr-3 pl-9 text-[length:var(--font-size-product-ui)] outline-none"
                  autoFocus
                />
                {storeSearch ? (
                  <IconButton
                    type="button"
                    onClick={() => {
                      setStoreSearch("");
                      searchRef.current?.focus();
                    }}
                    aria-label={t("actions.clearSearch")}
                    emphasis="ghost"
                    intent="neutral"
                    className="absolute top-1/2 right-1 -translate-y-1/2"
                  >
                    <Icon name="X" className="h-3.5 w-3.5" />
                  </IconButton>
                ) : null}
              </div>
            </div>

            <div className="border-border bg-background/80 flex shrink-0 items-center justify-between border-b px-3 py-2">
              <span className="text-muted-foreground text-[10px] font-semibold tracking-wider uppercase">
                {filteredStores.length} Locations
              </span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={handleSelectAllStores}
                  emphasis="ghost"
                  intent="brand"
                  size="touch"
                  className="px-2"
                >
                  Select All
                </Button>
                <Button
                  type="button"
                  onClick={handleSelectNoStores}
                  emphasis="ghost"
                  intent="neutral"
                  size="touch"
                  className="px-2"
                >
                  None
                </Button>
              </div>
            </div>

            <div className="min-h-[150px] flex-1 overflow-y-auto p-1">
              {filteredStores.length > 0 ? (
                filteredStores.map((store) => {
                  const isSelected = selectedStores.includes(store.id);

                  return (
                    <button
                      key={store.id}
                      type="button"
                      onClick={() => handleToggleStore(store.id)}
                      role="option"
                      aria-selected={isSelected}
                      className={`flex w-full cursor-pointer items-center gap-3 rounded-lg p-2.5 text-left transition-colors ${
                        isSelected
                          ? "bg-accent/10 hover:bg-accent/20"
                          : "hover:bg-card"
                      }`}
                    >
                      <div
                        className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border transition-colors ${
                          isSelected
                            ? "border-primary bg-primary"
                            : "border-border bg-background"
                        }`}
                      >
                        {isSelected && (
                          <Icon name="Check" size={12} className="text-white" />
                        )}
                      </div>
                      <span
                        className={`text-sm leading-tight ${
                          isSelected ? "text-white" : "text-muted-foreground"
                        }`}
                      >
                        {store.name}
                      </span>
                    </button>
                  );
                })
              ) : (
                <div className="text-muted-foreground p-4 text-center text-xs">
                  No stores found matching &quot;{storeSearch}&quot;
                </div>
              )}
            </div>

            <div className="border-border bg-background sticky bottom-0 z-10 border-t p-2">
              <Button
                emphasis="solid"
                intent="brand"
                size="touch"
                type="button"
                onClick={handleCloseDropdown}
                className="w-full gap-2 py-2"
              >
                <Icon name="Check" size={14} />
                Save Selection ({selectedStores.length})
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParticipatingStoresSelector;
